/**
 * Custom Problem Editor
 * Allows users to create and edit their own integer programming problems
 */

import { IntegerProblem, Inequality } from '../core/cuts'
import { buildInequalitiesFromVertices, SimpleVertex } from '../core/vertices-to-inequalities'
import { parseRational, normalizeRationalProblemToInteger, type RationalProblem, type RationalInequalityFrac } from '../core/rational'
import { Fraction, formatFractionForDisplay } from '../core/fraction'
import { logger } from '@/app/logger'

export class CustomProblemEditor {
  private editorContainer: HTMLElement | null = null
  private onProblemCreated: ((problem: IntegerProblem) => void) | null = null
  private currentMode: 'H' | 'V' = 'H'

  constructor() {
    logger.log('[CustomEditor] Constructor called');
    logger.log('[CustomEditor] Creating editor UI...');
    this.createEditorUI()
    logger.log('[CustomEditor] Constructor completed');
  }

  private createEditorUI(): void {
    logger.log('[CustomEditor] createEditorUI called');

    this.editorContainer = document.createElement('div')
    this.editorContainer.id = 'custom-editor'

    logger.log('[CustomEditor] Editor container created:', this.editorContainer);

    this.editorContainer.innerHTML = `
      <div class="editor-overlay">
        <div class="editor-panel ui-panel">
          <div class="panel-header">
            <h4>Custom Problem Editor</h4>
            <div class="header-actions">
              <button id="reset-editor" class="reset-btn">Reset</button>
            </div>
            <div class="header-controls">
              <button id="close-editor" class="panel-toggle-btn custom-editor-close" title="Close editor">×</button>
            </div>
          </div>
          <div class="panel-content">
            <div class="editor-section">
              <h5>max <span class="hint-text">(Use ↑↓ keys to adjust values)</span></h5>
              <div class="objective-input">
                <input type="text" id="obj-c1" placeholder="x coeff" value="1">
                <span>x +</span>
                <input type="text" id="obj-c2" placeholder="y coeff" value="1">
                <span>y +</span>
                <input type="text" id="obj-c3" placeholder="z coeff" value="1">
                <span>z</span>
              </div>
            </div>
            
            <div class="editor-section">
              <h5>Problem Formulation</h5>
              <div class="mode-toggle">
                <button id="mode-h-description" class="mode-btn active">H-description (Ax ≤ b)</button>
                <button id="mode-v-description" class="mode-btn">V-description (Vertices)</button>
              </div>

              <!-- H-description: halfspaces (constraints) -->
              <div id="formulation-h-section">
                <h5 class="mode-subtitle">Constraints <span class="hint-text">(Use ↑↓ keys to adjust values)</span></h5>
                <div id="constraints-container">
                  <div class="constraint-input">
                    <div class="constraint-main">
                      <div class="term">
                        <input type="text" class="coeff-input" placeholder="a" value="3">
                        <span>x +</span>
                      </div>
                      <div class="term">
                        <input type="text" class="coeff-input" placeholder="b" value="1">
                        <span>y +</span>
                      </div>
                      <div class="term">
                        <input type="text" class="coeff-input" placeholder="c" value="1">
                        <span>z ≤</span>
                      </div>
                      <input type="text" class="rhs-input" placeholder="rhs" value="7">
                    </div>
                    <button class="remove-constraint">🗑️</button>
                  </div>
                  <div class="constraint-input">
                    <div class="constraint-main">
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="a" value="1">
                        <span>x +</span>
                      </div>
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="b" value="3">
                        <span>y +</span>
                      </div>
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="c" value="1">
                        <span>z ≤</span>
                      </div>
                      <input type="text" class="rhs-input" placeholder="rhs" value="7">
                    </div>
                    <button class="remove-constraint">🗑️</button>
                  </div>
                  <div class="constraint-input">
                    <div class="constraint-main">
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="a" value="1">
                        <span>x +</span>
                      </div>
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="b" value="1">
                        <span>y +</span>
                      </div>
                      <div class="term">
                      <input type="text" class="coeff-input" placeholder="c" value="3">
                        <span>z ≤</span>
                      </div>
                      <input type="text" class="rhs-input" placeholder="rhs" value="7">
                    </div>
                    <button class="remove-constraint">🗑️</button>
                  </div>
                </div>
                <div class="constraint-actions">
                  <button id="add-constraint" class="add-btn">Add Constraint</button>
                </div>
                <div class="nonneg-section">
                  <h5>Variable Lower Bounds</h5>
                  <div class="nonneg-grid">
                    <label class="check"><input type="checkbox" id="nn-x" checked> x ≥ 0</label>
                    <label class="check"><input type="checkbox" id="nn-y" checked> y ≥ 0</label>
                    <label class="check"><input type="checkbox" id="nn-z" checked> z ≥ 0</label>
                  </div>
                </div>
              </div>

              <!-- V-description: vertices -->
              <div id="formulation-v-section" style="display: none;">
                <h5 class="mode-subtitle">Vertices (convex hull V-description)</h5>
                <p class="hint-text">Enter vertices of the polyhedron. The game will take the convex hull conv(V) and derive equivalent halfspaces α·x ≤ β.</p>
                <div id="vertices-container">
                  <div class="vertex-input">
                    <div class="vertex-main">
                      <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
                      <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
                      <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
                    </div>
                    <button class="remove-vertex">🗑️</button>
                  </div>
                  <div class="vertex-input">
                    <div class="vertex-main">
                      <input type="text" class="vertex-coord vertex-x" placeholder="x" value="1">
                      <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
                      <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
                    </div>
                    <button class="remove-vertex">🗑️</button>
                  </div>
                  <div class="vertex-input">
                    <div class="vertex-main">
                      <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
                      <input type="text" class="vertex-coord vertex-y" placeholder="y" value="1">
                      <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
                    </div>
                    <button class="remove-vertex">🗑️</button>
                  </div>
                  <div class="vertex-input">
                    <div class="vertex-main">
                      <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
                      <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
                      <input type="text" class="vertex-coord vertex-z" placeholder="z" value="1">
                    </div>
                    <button class="remove-vertex">🗑️</button>
                  </div>
                </div>
                <div class="vertex-actions">
                  <button id="add-vertex" class="add-btn">Add Vertex</button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="editor-actions">
            <button id="create-problem" class="create-btn">Create Problem</button>
          </div>
        </div>
      </div>
    `

    logger.log('[CustomEditor] HTML content set');

    // Add CSS styles - using consistent styling with main UI
    const style = document.createElement('style')
    style.textContent = `
      #custom-editor {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        z-index: 10000;
        display: none;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(4px);
      }

      #custom-editor.visible {
        display: flex;
      }

      #custom-editor .editor-panel {
        background: #0f172a; /* slate-900 */
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 16px;
        border-radius: 16px;
        width: 500px;
        /* height: auto; Default behavior */
        max-width: 95vw;
        max-height: 85vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(20px);
        pointer-events: auto;
        color: #e2e8f0; /* slate-200 */
      }

      #custom-editor .panel-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 24px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        background: rgba(0, 0, 0, 0.2);
      }

      #custom-editor .panel-header h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: #e2e8f0; /* slate-200 */
        letter-spacing: 0.025em;
      }

      #custom-editor .header-actions {
        margin-right: auto;
        margin-left: 16px;
      }

      #custom-editor .panel-content {
        padding: 24px;
      }

      .editor-section {
        margin-bottom: 32px;
      }

      .editor-section h5 {
        color: #94a3b8; /* slate-400 */
        margin-bottom: 12px;
        font-size: 13px;
        font-weight: 700;
        text-transform: none;
        letter-spacing: 0.05em;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .hint-text {
        font-size: 11px;
        color: #64748b; /* slate-500 */
        font-weight: normal;
        text-transform: none;
        letter-spacing: normal;
      }

      /* Objective Input */
      .objective-input {
        display: flex;
        align-items: center;
        gap: 8px;
        background: rgba(0, 0, 0, 0.2);
        padding: 16px;
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.05);
        justify-content: center;
      }

      .objective-input input {
        width: 60px;
        padding: 8px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        background: rgba(15, 23, 42, 0.6);
        color: #fff;
        text-align: center;
        font-size: 14px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        transition: all 0.2s;
      }

      .objective-input span {
        color: #94a3b8; /* slate-400 */
        font-weight: 600;
        font-size: 14px;
      }

      /* Mode Toggle */
      .mode-toggle {
        display: flex;
        background: rgba(0, 0, 0, 0.3);
        padding: 4px;
        border-radius: 10px;
        margin-bottom: 20px;
        border: 1px solid rgba(255, 255, 255, 0.05);
      }

      .mode-btn {
        flex: 1;
        border: none;
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        background: transparent;
        color: #64748b; /* slate-500 */
        transition: all 0.2s;
      }

      .mode-btn.active {
        background: rgba(56, 189, 248, 0.1); /* sky-500/10 */
        color: #38bdf8; /* sky-400 */
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }

      /* Constraints & Vertices Lists */
      #constraints-container, #vertices-container {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center; /* Prevent rows from stretching */
      }

      .constraint-input, .vertex-input {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        background: rgba(30, 41, 59, 0.4); /* slate-800/40 */
        border: 1px solid rgba(255, 255, 255, 0.05);
        border-radius: 10px;
        transition: all 0.2s;
      }

      .constraint-input:hover, .vertex-input:hover {
        background: rgba(30, 41, 59, 0.6);
        border-color: rgba(255, 255, 255, 0.1);
      }

      .constraint-main, .vertex-main {
        display: flex;
        align-items: center;
        gap: 4px;
        /* flex: 1; Removed to prevent stretching */
        justify-content: center;
      }

      .term {
        display: flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
      }

      .coeff-input, .rhs-input, .vertex-coord {
        width: 64px;
        padding: 6px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.2);
        color: #fff;
        text-align: center;
        font-size: 13px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        transition: all 0.2s;
      }

      .constraint-input span {
        color: #64748b; /* slate-500 */
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
      }

      /* Buttons */
      .remove-constraint, .remove-vertex {
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(255, 255, 255, 0.05);
        background: rgba(0, 0, 0, 0.2);
        border-radius: 6px;
        color: #64748b;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 14px;
      }

      .remove-constraint:hover, .remove-vertex:hover {
        background: rgba(239, 68, 68, 0.1); /* red-500/10 */
        color: #f87171; /* red-400 */
        border-color: rgba(239, 68, 68, 0.2);
      }

      .add-btn {
        width: 100%;
        padding: 10px;
        margin-top: 12px;
        background: rgba(16, 185, 129, 0.1); /* emerald-500/10 */
        border: 1px dashed rgba(16, 185, 129, 0.3);
        color: #34d399; /* emerald-400 */
        border-radius: 8px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
      }

      .add-btn:hover {
        background: rgba(16, 185, 129, 0.15);
        border-color: rgba(16, 185, 129, 0.5);
      }

      /* Footer Actions */
      .editor-actions {
        padding: 20px 24px;
        border-top: 1px solid rgba(255, 255, 255, 0.05);
        background: rgba(0, 0, 0, 0.2);
        display: flex;
        justify-content: center;
        gap: 12px;
      }

      .create-btn {
        background: #0284c7; /* sky-600 */
        color: white;
        border: none;
        padding: 10px 24px;
        border-radius: 8px;
        font-weight: 600;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .create-btn:hover {
        background: #0ea5e9; /* sky-500 */
        transform: translateY(-1px);
        box-shadow: 0 10px 15px -3px rgba(2, 132, 199, 0.2);
      }

      .reset-btn {
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: #94a3b8; /* slate-400 */
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .reset-btn:hover {
        background: rgba(255, 255, 255, 0.05);
        color: #e2e8f0;
      }

      /* Close Button */
      .custom-editor-close {
        background: transparent;
        border: none;
        color: #64748b;
        font-size: 20px;
        cursor: pointer;
        padding: 4px;
        border-radius: 6px;
        line-height: 1;
        transition: all 0.2s;
      }

      .custom-editor-close:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #e2e8f0;
      }

      /* Input Focus States */
      input[type="number"]:focus,
      input[type="text"]:focus {
        outline: none;
        border-color: #38bdf8 !important; /* sky-400 */
        box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.2) !important;
      }

      /* Non-negativity Section */
      .nonneg-section {
        margin-top: 24px;
        padding-top: 20px;
        border-top: 1px solid rgba(255, 255, 255, 0.05);
      }

      .nonneg-grid {
        display: flex;
        gap: 24px;
        justify-content: center;
      }

      .check {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #94a3b8;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
      }

      .check input {
        accent-color: #0ea5e9; /* sky-500 */
        width: 16px;
        height: 16px;
      }
    `

    logger.log('[CustomEditor] CSS styles created');

    document.head.appendChild(style)
    logger.log('[CustomEditor] CSS styles appended to head');

    document.body.appendChild(this.editorContainer)
    logger.log('[CustomEditor] Editor container appended to body');

    this.setupEventListeners()
    logger.log('[CustomEditor] Event listeners setup completed');
  }

  private setupEventListeners(): void {
    logger.log('[CustomEditor] Setting up event listeners...');

    const closeBtn = document.getElementById('close-editor')
    const addConstraintBtn = document.getElementById('add-constraint')
    const addVertexBtn = document.getElementById('add-vertex')
    const createBtn = document.getElementById('create-problem')
    const resetBtn = document.getElementById('reset-editor')
    const modeHBtn = document.getElementById('mode-h-description')
    const modeVBtn = document.getElementById('mode-v-description')

    logger.log('[CustomEditor] Found elements:', {
      closeBtn: !!closeBtn,
      addConstraintBtn: !!addConstraintBtn,
      createBtn: !!createBtn,
      resetBtn: !!resetBtn
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        logger.log('[CustomEditor] Close button clicked');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.hide();
      });
      logger.log('[CustomEditor] Close button event listener attached');
    } else {
      logger.error('[CustomEditor] Close button not found!');
    }

    addConstraintBtn?.addEventListener('click', () => this.addConstraint())
    addVertexBtn?.addEventListener('click', () => this.addVertex())
    createBtn?.addEventListener('click', () => this.createProblem())
    resetBtn?.addEventListener('click', () => this.resetEditor())

    modeHBtn?.addEventListener('click', () => this.setDescriptionMode('H'))
    modeVBtn?.addEventListener('click', () => this.setDescriptionMode('V'))

    // Enhanced keyboard support for number inputs
    this.setupNumberInputKeyboardSupport()

    // Delegated click handling for dynamic elements
    this.editorContainer?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.classList.contains('remove-constraint')) {
        this.removeConstraint(target)
      } else if (target.classList.contains('remove-vertex')) {
        this.removeVertex(target)
      }
    })

    // Add backdrop click to close modal
    this.editorContainer?.addEventListener('click', (e) => {
      if (e.target === this.editorContainer) {
        logger.log('[CustomEditor] Backdrop clicked, closing modal');
        this.hide();
      }
    })

    // Add escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.editorContainer?.classList.contains('visible')) {
        logger.log('[CustomEditor] Escape key pressed, closing modal');
        this.hide();
      }
    })

    logger.log('[CustomEditor] Event listeners setup completed');

    // Ensure initial mode is H-description
    this.setDescriptionMode('H')
  }

  /**
   * Setup enhanced keyboard support for number inputs
   */
  private setupNumberInputKeyboardSupport(): void {
    this.editorContainer?.addEventListener('keydown', (e) => {
      const target = e.target as HTMLInputElement

      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
      if (!target || target.tagName !== 'INPUT') return

      const type = target.type
      if (type !== 'text' && type !== 'number') return

      const raw = (target.value || '').trim()
      if (raw === '') return

      // Only handle pure floats like "1", "-2.5", "0.5". Skip fractional forms like "1/2".
      if (!/^[+-]?\d*\.?\d*$/.test(raw)) return

      const currentValue = Number(raw)
      if (!Number.isFinite(currentValue)) return

      e.preventDefault()

      const stepAttr = target.getAttribute('step')
      const baseStep = (() => {
        if (!stepAttr || stepAttr.trim() === '') return 1
        const s = Number(stepAttr)
        return Number.isFinite(s) && s > 0 ? s : 1
      })()

      const minAttr = target.getAttribute('min')
      const maxAttr = target.getAttribute('max')
      const min = minAttr != null && minAttr.trim() !== '' ? Number(minAttr) : -Infinity
      const max = maxAttr != null && maxAttr.trim() !== '' ? Number(maxAttr) : Infinity

      let step = baseStep
      if (e.shiftKey) {
        step = baseStep * 10
      } else if (e.ctrlKey || e.metaKey) {
        step = baseStep * 0.1
      }

      let newValue = currentValue
      if (e.key === 'ArrowUp') {
        newValue = Math.min(currentValue + step, max)
      } else if (e.key === 'ArrowDown') {
        newValue = Math.max(currentValue - step, min)
      }

      const decimalPlaces = Math.max(0, -Math.floor(Math.log10(step)))
      const factor = Math.pow(10, decimalPlaces)
      newValue = Math.round(newValue * factor) / factor

      target.value = newValue.toString()

      const inputEvent = new Event('input', { bubbles: true })
      target.dispatchEvent(inputEvent)

      target.classList.add('keyboard-adjusted')
      if (e.shiftKey) {
        target.classList.add('fast-adjust')
      } else if (e.ctrlKey || e.metaKey) {
        target.classList.add('slow-adjust')
      }

      setTimeout(() => {
        target.classList.remove('keyboard-adjusted', 'fast-adjust', 'slow-adjust')
      }, 300)
    })

    // Add tooltip on focus for better UX
    this.editorContainer?.addEventListener('focusin', (e) => {
      const target = e.target as HTMLInputElement
      if (!target || target.tagName !== 'INPUT') return

      const raw = (target.value || '').trim()
      const isNumericLike = raw === '' || /^[+-]?\d*\.?\d*$/.test(raw)
      if (!isNumericLike) return

      target.setAttribute(
        'title',
        'Use ↑↓ keys to adjust values\nShift+↑↓ for faster adjustment\nCtrl+↑↓ for slower adjustment'
      )
    })
  }

  show(currentProblem?: IntegerProblem): void {
    logger.log('[CustomEditor] show() called with problem:', currentProblem);

    if (this.editorContainer) {
      logger.log('[CustomEditor] Editor container exists:', this.editorContainer);

      // Store current problem and populate editor with it
      if (currentProblem) {
        this.populateEditorWithProblem(currentProblem)
      }

      // Default to H-description when opening the editor
      this.currentMode = 'H'
      this.setDescriptionMode('H')

      // Ensure the container is visible
      this.editorContainer.style.display = 'flex';
      this.editorContainer.classList.add('visible')

      logger.log('[CustomEditor] Editor container classes:', this.editorContainer.classList.toString());
      logger.log('[CustomEditor] Editor container style display:', this.editorContainer.style.display);

      // Force a reflow to ensure the element is properly displayed
      this.editorContainer.offsetHeight;

      logger.log('[CustomEditor] Editor should now be visible');
    } else {
      logger.error('[CustomEditor] Editor container not found!');
    }
  }

  hide(): void {
    logger.log('[CustomEditor] hide() called');

    if (this.editorContainer) {
      logger.log('[CustomEditor] Hiding editor container');
      this.editorContainer.classList.remove('visible')
      this.editorContainer.style.display = 'none';

      logger.log('[CustomEditor] Editor container classes:', this.editorContainer.classList.toString());
      logger.log('[CustomEditor] Editor container style display:', this.editorContainer.style.display);
      logger.log('[CustomEditor] Editor should now be hidden');
    } else {
      logger.error('[CustomEditor] Editor container not found!');
    }
  }

  onProblemCreate(callback: (problem: IntegerProblem) => void): void {
    this.onProblemCreated = callback
  }

  /**
   * Switch between H-description (halfspaces) and V-description (vertices) modes.
   */
  private setDescriptionMode(mode: 'H' | 'V'): void {
    this.currentMode = mode

    if (!this.editorContainer) return

    const hSection = this.editorContainer.querySelector('#formulation-h-section') as HTMLElement | null
    const vSection = this.editorContainer.querySelector('#formulation-v-section') as HTMLElement | null
    const hBtn = this.editorContainer.querySelector('#mode-h-description') as HTMLButtonElement | null
    const vBtn = this.editorContainer.querySelector('#mode-v-description') as HTMLButtonElement | null

    // Update UI
    if (mode === 'H') {
      this.editorContainer?.querySelector('.editor-panel')?.classList.add('mode-h')

      if (hSection) hSection.style.display = 'block'
      if (vSection) vSection.style.display = 'none'

      if (hBtn) hBtn.classList.add('active')
      if (vBtn) vBtn.classList.remove('active')
    } else {
      this.editorContainer?.querySelector('.editor-panel')?.classList.remove('mode-h')

      if (hSection) hSection.style.display = 'none'
      if (vSection) vSection.style.display = 'block'

      if (hBtn) hBtn.classList.remove('active')
      if (vBtn) vBtn.classList.add('active')
    }
  }

  /**
   * Populate editor with current problem constraints
   */
  private populateEditorWithProblem(problem: IntegerProblem): void {
    logger.log('[CustomEditor] Populating editor with problem:', problem)

    const fracData = (problem as any).fracData as RationalProblem | undefined

    // Set objective coefficients
    const objC1 = document.getElementById('obj-c1') as HTMLInputElement
    const objC2 = document.getElementById('obj-c2') as HTMLInputElement
    const objC3 = document.getElementById('obj-c3') as HTMLInputElement

    if (fracData) {
      if (objC1 && fracData.c[0] !== undefined) objC1.value = formatFractionForDisplay(fracData.c[0])
      if (objC2 && fracData.c[1] !== undefined) objC2.value = formatFractionForDisplay(fracData.c[1])
      if (objC3 && fracData.c[2] !== undefined) objC3.value = formatFractionForDisplay(fracData.c[2])
    } else {
      if (objC1 && problem.c[0] !== undefined) objC1.value = problem.c[0].toString()
      if (objC2 && problem.c[1] !== undefined) objC2.value = problem.c[1].toString()
      if (objC3 && problem.c[2] !== undefined) objC3.value = problem.c[2].toString()
    }

    // Clear existing constraints
    const container = document.getElementById('constraints-container')
    if (!container) return

    container.innerHTML = ''

    // Build display constraints, preferring fracData when available, and exclude non-negativity rows.
    const displayConstraints = (() => {
      if (fracData) {
        return fracData.inequalities
          .map((ineq) => {
            const alphaNum = ineq.alpha.map((a) => a.toNumber())
            const betaNum = ineq.beta.toNumber()
            const isNonNegativity =
              alphaNum.filter((c) => c !== 0).length === 1 &&
              alphaNum.some((c) => c === -1) &&
              betaNum === 0
            if (isNonNegativity) return null
            return {
              alphaStr: ineq.alpha.map((a) => formatFractionForDisplay(a)),
              betaStr: formatFractionForDisplay(ineq.beta),
            }
          })
          .filter((x) => x !== null) as Array<{ alphaStr: string[]; betaStr: string }>
      }
      return (problem.inequalities || [])
        .map((ineq) => {
          const alpha = ineq.alpha || []
          const isNonNegativity =
            alpha.filter((c) => c !== 0).length === 1 &&
            alpha.some((c) => c === -1) &&
            ineq.beta === 0
          if (isNonNegativity) return null
          return {
            alphaStr: alpha.map((a) => a.toString()),
            betaStr: ineq.beta.toString(),
          }
        })
        .filter((x) => x !== null) as Array<{ alphaStr: string[]; betaStr: string }>
    })()

    logger.log('[CustomEditor] User constraints to populate:', displayConstraints)

    if (displayConstraints.length === 0) {
      // Add default constraints if no user constraints
      this.resetEditor()
      return
    }

    displayConstraints.forEach(constraint => {
      const constraintDiv = document.createElement('div')
      constraintDiv.className = 'constraint-input'

      const a = constraint.alphaStr[0] ?? '0'
      const b = constraint.alphaStr[1] ?? '0'
      const c = constraint.alphaStr[2] ?? '0'
      const rhs = constraint.betaStr ?? '0'

      constraintDiv.innerHTML = `
        <div class="constraint-main">
          <div class="term">
            <input type="text" class="coeff-input" placeholder="a" value="${a}">
            <span>x +</span>
          </div>
          <div class="term">
            <input type="text" class="coeff-input" placeholder="b" value="${b}">
            <span>y +</span>
          </div>
          <div class="term">
            <input type="text" class="coeff-input" placeholder="c" value="${c}">
            <span>z ≤</span>
          </div>
          <input type="text" class="rhs-input" placeholder="rhs" value="${rhs}">
        </div>
        <button class="remove-constraint">🗑️</button>
      `

      container.appendChild(constraintDiv)
    })

    logger.log('[CustomEditor] Populated editor with', displayConstraints.length, 'constraints')

    // Set non-negativity toggles according to incoming problem
    const hasX = problem.inequalities?.some(q => q.alpha[0] === -1 && q.alpha[1] === 0 && q.alpha[2] === 0 && q.beta === 0)
    const hasY = problem.inequalities?.some(q => q.alpha[1] === -1 && q.alpha[0] === 0 && q.alpha[2] === 0 && q.beta === 0)
    const hasZ = problem.inequalities?.some(q => q.alpha[2] === -1 && q.alpha[0] === 0 && q.alpha[1] === 0 && q.beta === 0)
    const nnx = this.editorContainer?.querySelector('#nn-x') as HTMLInputElement | null
    const nny = this.editorContainer?.querySelector('#nn-y') as HTMLInputElement | null
    const nnz = this.editorContainer?.querySelector('#nn-z') as HTMLInputElement | null
    if (nnx) nnx.checked = !!hasX
    if (nny) nny.checked = !!hasY
    if (nnz) nnz.checked = !!hasZ
  }

  private addConstraint(): void {
    const container = document.getElementById('constraints-container')
    if (!container) return

    const constraintDiv = document.createElement('div')
    constraintDiv.className = 'constraint-input'
    constraintDiv.innerHTML = `
      <div class="constraint-main">
        <div class="term">
          <input type="text" class="coeff-input" placeholder="a" value="1">
          <span>x +</span>
        </div>
        <div class="term">
          <input type="text" class="coeff-input" placeholder="b" value="1">
          <span>y +</span>
        </div>
        <div class="term">
          <input type="text" class="coeff-input" placeholder="c" value="1">
          <span>z ≤</span>
        </div>
        <input type="text" class="rhs-input" placeholder="rhs" value="1">
      </div>
      <button class="remove-constraint">🗑️</button>
    `

    container.appendChild(constraintDiv)
  }

  private addVertex(): void {
    const container = document.getElementById('vertices-container')
    if (!container) return

    const vertexDiv = document.createElement('div')
    vertexDiv.className = 'vertex-input'
    vertexDiv.innerHTML = `
      <div class="vertex-main">
        <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
        <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
        <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
      </div>
      <button class="remove-vertex">🗑️</button>
    `

    container.appendChild(vertexDiv)
  }

  private removeConstraint(button: HTMLElement): void {
    const constraintDiv = button.closest('.constraint-input')
    const container = document.getElementById('constraints-container')

    // Don't allow removing if only one constraint remains
    if (container && container.children.length > 1) {
      constraintDiv?.remove()
    }
  }

  private removeVertex(button: HTMLElement): void {
    const vertexDiv = button.closest('.vertex-input')
    const container = document.getElementById('vertices-container')

    if (container && container.children.length > 1) {
      vertexDiv?.remove()
    }
  }

  private extractProblemFromUI(): IntegerProblem {
    if (!this.editorContainer) {
      throw new Error('Editor container not found')
    }

    const c1Frac = parseRational((this.editorContainer.querySelector('#obj-c1') as HTMLInputElement)?.value || '1')
    const c2Frac = parseRational((this.editorContainer.querySelector('#obj-c2') as HTMLInputElement)?.value || '1')
    const c3Frac = parseRational((this.editorContainer.querySelector('#obj-c3') as HTMLInputElement)?.value || '1')
    const cFrac = [c1Frac, c2Frac, c3Frac]

    const constraintInputs = this.editorContainer.querySelectorAll('.constraint-input')
    const A_frac: Fraction[][] = []
    const b_frac: Fraction[] = []
    const inequalities_frac: RationalInequalityFrac[] = []

    constraintInputs.forEach((input) => {
      const coeffInputs = input.querySelectorAll('.coeff-input') as NodeListOf<HTMLInputElement>
      const rhsInput = input.querySelector('.rhs-input') as HTMLInputElement | null

      if (coeffInputs.length >= 3 && rhsInput) {
        const a1 = parseRational(coeffInputs[0].value)
        const a2 = parseRational(coeffInputs[1].value)
        const a3 = parseRational(coeffInputs[2].value)
        const rhs = parseRational(rhsInput.value)

        const alpha = [a1, a2, a3]
        A_frac.push(alpha)
        b_frac.push(rhs)
        inequalities_frac.push({ alpha, beta: rhs })
      }
    })

    // Add non-negativity constraints if toggled
    const nnx = (this.editorContainer.querySelector('#nn-x') as HTMLInputElement)?.checked !== false
    const nny = (this.editorContainer.querySelector('#nn-y') as HTMLInputElement)?.checked !== false
    const nnz = (this.editorContainer.querySelector('#nn-z') as HTMLInputElement)?.checked !== false

    if (nnx) {
      const alpha = [new Fraction(-1), Fraction.zero(), Fraction.zero()]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }
    if (nny) {
      const alpha = [Fraction.zero(), new Fraction(-1), Fraction.zero()]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }
    if (nnz) {
      const alpha = [Fraction.zero(), Fraction.zero(), new Fraction(-1)]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }

    const rp: RationalProblem = {
      A: A_frac,
      b: b_frac,
      c: cFrac,
      inequalities: inequalities_frac,
    }

    const ip = normalizeRationalProblemToInteger(rp)
    return ip
  }

  /**
   * Extract problem in V-description mode (from vertices).
   * We take convex hull of vertices (via supporting hyperplanes) and translate to Ax <= b.
   */
  private extractProblemFromVertices(): IntegerProblem {
    if (!this.editorContainer) {
      throw new Error('Editor container not found')
    }

    const c1Frac = parseRational((this.editorContainer.querySelector('#obj-c1') as HTMLInputElement)?.value || '1')
    const c2Frac = parseRational((this.editorContainer.querySelector('#obj-c2') as HTMLInputElement)?.value || '1')
    const c3Frac = parseRational((this.editorContainer.querySelector('#obj-c3') as HTMLInputElement)?.value || '1')
    const cFrac = [c1Frac, c2Frac, c3Frac]

    const vertexRows = this.editorContainer.querySelectorAll('.vertex-input')
    const vertices: SimpleVertex[] = []

    vertexRows.forEach((row, index) => {
      const xInput = row.querySelector('.vertex-x') as HTMLInputElement | null
      const yInput = row.querySelector('.vertex-y') as HTMLInputElement | null
      const zInput = row.querySelector('.vertex-z') as HTMLInputElement | null

      if (!xInput || !yInput || !zInput) {
        logger.warn(`[CustomEditor] Vertex row ${index} missing coordinate inputs`, row)
        return
      }

      const xFrac = parseRational(xInput.value || '0')
      const yFrac = parseRational(yInput.value || '0')
      const zFrac = parseRational(zInput.value || '0')

      vertices.push({ x: xFrac.toNumber(), y: yFrac.toNumber(), z: zFrac.toNumber() })
    })

    if (vertices.length < 4) {
      throw new Error('V-description requires at least 4 vertices to form a 3D polyhedron.')
    }

    const inequalitiesFromVertices = buildInequalitiesFromVertices(vertices)

    const A_frac: Fraction[][] = []
    const b_frac: Fraction[] = []
    const inequalities_frac: RationalInequalityFrac[] = []

    inequalitiesFromVertices.forEach((ineq) => {
      const alpha = [
        Fraction.fromDecimal(ineq.alpha[0] ?? 0),
        Fraction.fromDecimal(ineq.alpha[1] ?? 0),
        Fraction.fromDecimal(ineq.alpha[2] ?? 0),
      ]
      const beta = Fraction.fromDecimal(ineq.beta)
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    })

    // Optional non-negativity constraints (same toggles as H-description)
    const nnx = (this.editorContainer.querySelector('#nn-x') as HTMLInputElement)?.checked !== false
    const nny = (this.editorContainer.querySelector('#nn-y') as HTMLInputElement)?.checked !== false
    const nnz = (this.editorContainer.querySelector('#nn-z') as HTMLInputElement)?.checked !== false
    if (nnx) {
      const alpha = [new Fraction(-1), Fraction.zero(), Fraction.zero()]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }
    if (nny) {
      const alpha = [Fraction.zero(), new Fraction(-1), Fraction.zero()]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }
    if (nnz) {
      const alpha = [Fraction.zero(), Fraction.zero(), new Fraction(-1)]
      const beta = Fraction.zero()
      A_frac.push(alpha)
      b_frac.push(beta)
      inequalities_frac.push({ alpha, beta })
    }

    const rp: RationalProblem = {
      A: A_frac,
      b: b_frac,
      c: cFrac,
      inequalities: inequalities_frac,
    }

    const ip = normalizeRationalProblemToInteger(rp)
    return ip
  }

  private checkProblemValidity(problem: IntegerProblem): Array<{ type: 'error' | 'warning', message: string }> {
    const issues: Array<{ type: 'error' | 'warning', message: string }> = []

    // Check for NaN values
    if (problem.c.some(isNaN)) {
      issues.push({ type: 'error', message: 'Objective function coefficients contain invalid values' })
    }

    if (problem.A.some(row => row.some(isNaN))) {
      issues.push({ type: 'error', message: 'Constraint matrix contains invalid values' })
    }

    if (problem.b.some(isNaN)) {
      issues.push({ type: 'error', message: 'Constraint right-hand side contains invalid values' })
    }

    // Check for zero objective
    if (problem.c.every(coeff => coeff === 0)) {
      issues.push({ type: 'warning', message: 'All objective function coefficients are zero' })
    }

    // Check for infeasible constraints
    if (problem.A.some((row, i) => row.every(coeff => coeff === 0) && problem.b[i] < 0)) {
      issues.push({ type: 'error', message: 'Infeasible constraint: 0 ≤ negative number' })
    }

    // Check for very large coefficients
    const maxCoeff = Math.max(...problem.A.flat(), ...problem.b, ...problem.c)
    if (maxCoeff > 1000) {
      issues.push({ type: 'warning', message: 'Large coefficients may affect numerical stability' })
    }

    return issues
  }

  private createProblem(): void {
    try {
      // Source of truth for mode is the currently active tab in the UI.
      // This prevents any stale this.currentMode state from causing us to
      // accidentally mix H/V formulations.
      let useVDescription = this.currentMode === 'V'
      if (this.editorContainer) {
        const vBtn = this.editorContainer.querySelector('#mode-v-description') as HTMLButtonElement | null
        const vSection = this.editorContainer.querySelector('#formulation-v-section') as HTMLElement | null
        const hSection = this.editorContainer.querySelector('#formulation-h-section') as HTMLElement | null
        if (vBtn && vBtn.classList.contains('active')) {
          useVDescription = true
        } else if (hSection && hSection.style.display === 'block') {
          useVDescription = false
        } else if (vSection && vSection.style.display === 'block') {
          useVDescription = true
        }
      }

      const problem = useVDescription
        ? this.extractProblemFromVertices()
        : this.extractProblemFromUI()
      logger.log('[CustomEditor] Extracted problem from UI:', problem)
      logger.log('[CustomEditor] Problem inequalities:', problem.inequalities)
      logger.log('[CustomEditor] First constraint:', problem.inequalities[0])

      const issues = this.checkProblemValidity(problem)

      const hasErrors = issues.some(issue => issue.type === 'error')
      if (hasErrors) {
        logger.warn('Problem has errors but creating anyway:', issues)
      }

      if (this.onProblemCreated) {
        logger.log('[CustomEditor] Calling onProblemCreated callback with problem:', problem)
        this.onProblemCreated(problem)
      }

      this.hide()
    } catch (error) {
      logger.error('Error creating problem:', error)
      alert('Error creating problem. Please check your inputs.')
    }
  }

  private resetEditor(): void {
    // Reset objective
    const objC1 = document.getElementById('obj-c1') as HTMLInputElement
    const objC2 = document.getElementById('obj-c2') as HTMLInputElement
    const objC3 = document.getElementById('obj-c3') as HTMLInputElement
    if (objC1) objC1.value = '1'
    if (objC2) objC2.value = '1'
    if (objC3) objC3.value = '1'

    // Reset H-description constraints to default
    const container = document.getElementById('constraints-container')
    if (container) {
      container.innerHTML = `
        <div class="constraint-input">
          <div class="constraint-main">
            <div class="term">
              <input type="text" class="coeff-input" placeholder="a" value="3">
              <span>x +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="b" value="1">
              <span>y +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="c" value="1">
              <span>z ≤</span>
            </div>
            <input type="text" class="rhs-input" placeholder="rhs" value="7">
          </div>
          <button class="remove-constraint">🗑️</button>
        </div>
        <div class="constraint-input">
          <div class="constraint-main">
            <div class="term">
              <input type="text" class="coeff-input" placeholder="a" value="1">
              <span>x +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="b" value="3">
              <span>y +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="c" value="1">
              <span>z ≤</span>
            </div>
            <input type="text" class="rhs-input" placeholder="rhs" value="7">
          </div>
          <button class="remove-constraint">🗑️</button>
        </div>
        <div class="constraint-input">
          <div class="constraint-main">
            <div class="term">
              <input type="text" class="coeff-input" placeholder="a" value="1">
              <span>x +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="b" value="1">
              <span>y +</span>
            </div>
            <div class="term">
              <input type="text" class="coeff-input" placeholder="c" value="3">
              <span>z ≤</span>
            </div>
            <input type="text" class="rhs-input" placeholder="rhs" value="7">
          </div>
          <button class="remove-constraint">🗑️</button>
        </div>
      `
    }

    // Reset V-description vertices to default tetrahedron
    const verticesContainer = document.getElementById('vertices-container')
    if (verticesContainer) {
      verticesContainer.innerHTML = `
        <div class="vertex-input">
          <div class="vertex-main">
            <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
            <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
            <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
          </div>
          <button class="remove-vertex">🗑️</button>
        </div>
        <div class="vertex-input">
          <div class="vertex-main">
            <input type="text" class="vertex-coord vertex-x" placeholder="x" value="1">
            <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
            <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
          </div>
          <button class="remove-vertex">🗑️</button>
        </div>
        <div class="vertex-input">
          <div class="vertex-main">
            <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
            <input type="text" class="vertex-coord vertex-y" placeholder="y" value="1">
            <input type="text" class="vertex-coord vertex-z" placeholder="z" value="0">
          </div>
          <button class="remove-vertex">🗑️</button>
        </div>
        <div class="vertex-input">
          <div class="vertex-main">
            <input type="text" class="vertex-coord vertex-x" placeholder="x" value="0">
            <input type="text" class="vertex-coord vertex-y" placeholder="y" value="0">
            <input type="text" class="vertex-coord vertex-z" placeholder="z" value="1">
          </div>
          <button class="remove-vertex">🗑️</button>
        </div>
      `
    }

    // Reset to H-description mode by default
    this.currentMode = 'H'
    this.setDescriptionMode('H')

    logger.log('[CustomEditor] Editor reset to default values')
  }
}

// Global custom editor instance
export const customProblemEditor = new CustomProblemEditor()
